import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { subscriberService } from '@/lib/api/services/subscriber'
import type {
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  Subscriber,
  SubscriberFilterParams,
  SubscriberStats
} from '@/lib/api/types'
import { showToast } from '@/lib/toast'

// Query keys for caching
export const subscriberKeys = {
  all: ['subscribers'] as const,
  lists: () => [...subscriberKeys.all, 'list'] as const,
  list: (filters?: SubscriberFilterParams) => [...subscriberKeys.lists(), filters] as const,
  active: () => [...subscriberKeys.all, 'active'] as const,
  paginated: (params: PaginationParams, filters: SubscriberFilterParams) =>
    [...subscriberKeys.all, 'paginated', params, filters] as const,
  stats: () => [...subscriberKeys.all, 'stats'] as const
}

// Hook to fetch all active subscribers
export function useActiveSubscribers() {
  return useQuery<ApiResponse<Subscriber[]>, Error>({
    queryKey: subscriberKeys.active(),
    queryFn: () => subscriberService.getAllActiveSubscribers(),
    staleTime: 5 * 60 * 1000 // 5 minutes
  })
}

// Hook to fetch paginated subscribers
export function usePaginatedSubscribers(
  params: PaginationParams = { page: 1, size: 10 },
  filters: SubscriberFilterParams = {}
) {
  return useQuery<ApiResponse<PaginatedResponse<Subscriber>>, Error>({
    queryKey: subscriberKeys.paginated(params, filters),
    queryFn: () => subscriberService.getPaginatedSubscribers(params, filters)
  })
}

// Hook to fetch total subscribers count
export function useTotalSubscribersCount() {
  return useQuery<ApiResponse<number>, Error>({
    queryKey: [...subscriberKeys.stats(), 'total'],
    queryFn: () => subscriberService.getTotalSubscribersCount(),
    retry: false,
    staleTime: 5 * 60 * 1000 // 5 minutes
  })
}

// Hook to fetch subscribers growth (last 30 days)
export function useSubscribersGrowth() {
  return useQuery<ApiResponse<number>, Error>({
    queryKey: [...subscriberKeys.stats(), 'growth'],
    queryFn: () => subscriberService.getSubscribersGrowth(),
    retry: false,
    staleTime: 5 * 60 * 1000 // 5 minutes
  })
}

// Hook to fetch subscriber stats
export function useSubscriberStats() {
  return useQuery<SubscriberStats, Error>({
    queryKey: subscriberKeys.stats(),
    queryFn: async () => {
      const response = await subscriberService.getSubscriberStats()
      return response.data
    },
    staleTime: 5 * 60 * 1000
  })
}

// Hook to unsubscribe a subscriber
export function useUnsubscribe() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<void>, Error, string>({
    mutationFn: (email: string) => subscriberService.unsubscribe(email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriberKeys.all })
      showToast.success('Subscriber unsubscribed successfully')
    },
    onError: (error: Error) => {
      showToast.error(error.message || 'Failed to unsubscribe')
    }
  })
}

